/**
 * ClientTelemetryService — Application Insights client SDK wrapper.
 *
 * Singleton that initializes the App Insights web SDK and provides
 * domain-specific logging methods. Pre-init queue buffers events
 * before SDK is ready.
 *
 * Dual-path: uses the web SDK in browser, safe no-ops NativeBridge
 * on Capacitor (the server handles native telemetry via FCM data).
 */

import { Capacitor } from "@capacitor/core"
import { LogCategory, SeverityLevel, type TelemetryEvent, type StandardDimensions } from "./types"

// ---------------------------------------------------------------------------
// Pre-init queue — buffers events before SDK loads
// ---------------------------------------------------------------------------

const PRE_INIT_QUEUE: TelemetryEvent[] = []
const MAX_QUEUE_SIZE = 50

let isInitialized = false
let appInsightsClient: AppInsightsClient | null = null

// Minimal interface for the App Insights API we use
interface AppInsightsClient {
  trackEvent(event: { name: string; properties?: Record<string, string> }): void
  trackException(exception: { exception: Error; severityLevel?: number; properties?: Record<string, string> }): void
  trackPageView(pageView: { name?: string; uri?: string; properties?: Record<string, string> }): void
  trackTrace(trace: { message: string; severityLevel?: number; properties?: Record<string, string> }): void
  trackMetric(metric: { name: string; average: number; properties?: Record<string, string> }): void
  flush(): void
  context: {
    user: { id: string; authenticatedId: string }
    session: { id: string }
    application: { ver: string }
  }
}

// ---------------------------------------------------------------------------
// Standard dimensions (populated on init)
// ---------------------------------------------------------------------------

let standardDimensions: StandardDimensions = {
  platform: Capacitor.getPlatform(),
  appVersion: "unknown",
  sessionId: crypto.randomUUID(),
}

function dimensionsAsStrings(): Record<string, string> {
  const d = standardDimensions
  const result: Record<string, string> = {
    platform: d.platform,
    appVersion: d.appVersion,
    sessionId: d.sessionId,
  }
  if (d.userId) result.userId = d.userId
  if (d.deviceId) result.deviceId = d.deviceId
  return result
}

// ---------------------------------------------------------------------------
// Initialize
// ---------------------------------------------------------------------------

export async function initializeTelemetry(options?: {
  connectionString?: string
  userId?: string
  deviceId?: string
  appVersion?: string
}): Promise<void> {
  if (isInitialized) return

  if (options?.userId) standardDimensions.userId = options.userId
  if (options?.deviceId) standardDimensions.deviceId = options.deviceId
  if (options?.appVersion) standardDimensions.appVersion = options.appVersion

  const connectionString =
    options?.connectionString ??
    (import.meta.env.VITE_APPLICATION_INSIGHTS_CONNECTION_STRING as string | undefined)

  if (!connectionString) {
    console.log("[Telemetry] No connection string — running in offline mode")
    isInitialized = true
    flushQueue()
    return
  }

  try {
    // Dynamic import to keep bundle small when not used
    const { ApplicationInsights } = await import(
      "@microsoft/applicationinsights-web"
    )

    const ai = new ApplicationInsights({
      config: {
        connectionString,
        enableAutoRouteTracking: true,
        disableFetchTracking: false,
        enableCorsCorrelation: true,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        // Sampling: 20% in prod, 100% in dev
        samplingPercentage:
          import.meta.env.MODE === "production" ? 20 : 100,
      },
    })

    ai.loadAppInsights()

    // Set user/session context
    if (options?.userId) {
      ai.context.user.authenticatedId = options.userId
      ai.context.user.id = options.userId
    }
    ai.context.session.id = standardDimensions.sessionId
    if (options?.appVersion) {
      ai.context.application.ver = options.appVersion
    }

    appInsightsClient = ai as unknown as AppInsightsClient
    isInitialized = true

    // Flush pre-init queue
    flushQueue()

    console.log("[Telemetry] Application Insights initialized")
  } catch (e) {
    console.warn("[Telemetry] Failed to initialize App Insights:", e)
    isInitialized = true // Don't retry — run in offline mode
    flushQueue()
  }
}

function flushQueue(): void {
  while (PRE_INIT_QUEUE.length > 0) {
    const event = PRE_INIT_QUEUE.shift()!
    trackEventInternal(event)
  }
}

// ---------------------------------------------------------------------------
// Core tracking methods
// ---------------------------------------------------------------------------

function trackEventInternal(event: TelemetryEvent): void {
  if (!appInsightsClient) return

  const props: Record<string, string> = {
    ...dimensionsAsStrings(),
    category: event.category,
  }

  if (event.properties) {
    for (const [key, value] of Object.entries(event.properties)) {
      if (value !== undefined) props[key] = String(value)
    }
  }

  appInsightsClient.trackEvent({ name: event.name, properties: props })

  // Track measurements separately
  if (event.measurements) {
    for (const [name, value] of Object.entries(event.measurements)) {
      appInsightsClient.trackMetric({
        name: `${event.name}.${name}`,
        average: value,
        properties: props,
      })
    }
  }
}

/**
 * Track a custom telemetry event.
 * If called before init, events are queued (up to 50).
 */
export function trackTelemetryEvent(event: TelemetryEvent): void {
  if (!isInitialized) {
    if (PRE_INIT_QUEUE.length < MAX_QUEUE_SIZE) {
      PRE_INIT_QUEUE.push(event)
    }
    return
  }
  trackEventInternal(event)
}

/**
 * Track an exception with optional severity and properties.
 */
export function trackException(
  error: Error,
  severity: SeverityLevel = SeverityLevel.Error,
  properties?: Record<string, string>,
): void {
  if (!appInsightsClient) return

  appInsightsClient.trackException({
    exception: error,
    severityLevel: severity,
    properties: { ...dimensionsAsStrings(), ...properties },
  })
}

/**
 * Track a page/screen view.
 */
export function trackPageView(name: string, uri?: string): void {
  if (!appInsightsClient) return

  appInsightsClient.trackPageView({
    name,
    uri,
    properties: dimensionsAsStrings(),
  })
}

/**
 * Track a trace message (structured log).
 */
export function trackTrace(
  message: string,
  severity: SeverityLevel = SeverityLevel.Information,
  properties?: Record<string, string>,
): void {
  if (!appInsightsClient) return

  appInsightsClient.trackTrace({
    message,
    severityLevel: severity,
    properties: { ...dimensionsAsStrings(), ...properties },
  })
}

/**
 * Set the authenticated user ID (call after login).
 */
export function setTelemetryUserId(userId: string): void {
  standardDimensions.userId = userId
  if (appInsightsClient) {
    appInsightsClient.context.user.authenticatedId = userId
    appInsightsClient.context.user.id = userId
  }
}

/**
 * Flush pending telemetry to the server.
 */
export function flushTelemetry(): void {
  appInsightsClient?.flush()
}

// ---------------------------------------------------------------------------
// Domain-specific convenience methods
// ---------------------------------------------------------------------------

export function logAuth(
  action: "login" | "logout" | "token_refresh" | "error",
  properties?: Record<string, string | number | boolean>,
): void {
  const category =
    action === "error" ? LogCategory.AUTH_ERROR :
    action === "logout" ? LogCategory.AUTH_LOGOUT :
    action === "token_refresh" ? LogCategory.AUTH_TOKEN :
    LogCategory.AUTH_LOGIN

  trackTelemetryEvent({
    name: `auth_${action}`,
    category,
    properties,
  })
}

export function logSync(
  action: "completed" | "failed" | "started",
  properties?: Record<string, string | number | boolean>,
): void {
  const category = action === "failed" ? LogCategory.DEXIE_ERROR : LogCategory.DEXIE_SYNC
  trackTelemetryEvent({
    name: `dexie_sync_${action}`,
    category,
    properties,
  })
}

export function logPush(
  action: "received" | "register" | "token" | "error",
  properties?: Record<string, string | number | boolean>,
): void {
  const category =
    action === "error" ? LogCategory.PUSH_ERROR :
    action === "register" ? LogCategory.PUSH_REGISTER :
    action === "token" ? LogCategory.PUSH_TOKEN :
    LogCategory.PUSH_RECEIVED

  trackTelemetryEvent({
    name: `push_${action}`,
    category,
    properties,
  })
}

export function logPerformance(
  name: string,
  durationMs: number,
  properties?: Record<string, string | number | boolean>,
): void {
  trackTelemetryEvent({
    name: `perf_${name}`,
    category: LogCategory.PERFORMANCE,
    properties,
    measurements: { durationMs },
  })
}
